import { getAuthSettings } from "@entrilia/shared/auth/settings";
import { getUrlHostSafe } from "@entrilia/shared/utilities/urlHelper";
import { getAnonymousAuthBaseUrl } from "../../api/accessCheckApi";
import type { BlobProbeTarget } from "../../api/types/accessCheckApiTypes";
import type { ConnectionCheckId, ConnectionCheckResult } from "./types";

const TIMEOUT_MS = 5000;

interface ProbeSpec {
  id: ConnectionCheckId;
  label: string;
  url: string;
  targetHost: string;
  init: RequestInit;
  passOn: "fetched" | "ok";
}

// Derives the B2C OIDC discovery URL for the configured sign-in user flow from
// the authorize endpoint we already use to redirect users to B2C. Format:
//   {origin}/{tenant-directory}/{policy}/v2.0/.well-known/openid-configuration
// where the tenant directory is the first path segment of the authorize URL
// (e.g., "<tenant>.onmicrosoft.com"). Returns undefined when settings are
// missing or unparsable so the B2C probe can be skipped rather than report a
// false negative against a hardcoded host.
const buildB2CDiscoveryUrl = (authEndpoint: string, userFlow: string): string | undefined => {
  try {
    const url = new URL(authEndpoint);
    const tenantDirectory = url.pathname.split("/").filter(Boolean)[0];
    if (!tenantDirectory) {
      return undefined;
    }
    return `${url.origin}/${tenantDirectory}/${userFlow}/v2.0/.well-known/openid-configuration`;
  } catch {
    return undefined;
  }
};

const buildSpecs = (): ProbeSpec[] => {
  const specs: ProbeSpec[] = [
    {
      id: "internet",
      label: "Public internet",
      url: "https://www.entrilia.com/favicon.ico",
      targetHost: "www.entrilia.com",
      init: { mode: "no-cors", cache: "no-store", credentials: "omit" },
      passOn: "fetched",
    },
    {
      id: "cdn",
      label: "Entrilia CDN",
      url: "https://cdn.entrilia.com/sso/pages/signin/template.html",
      targetHost: "cdn.entrilia.com",
      init: { mode: "no-cors", cache: "no-store", credentials: "omit" },
      passOn: "fetched",
    },
    {
      // Real cross-origin POST so the browser auto-fires a CORS preflight (with
      // Access-Control-Request-Method, which is what APIM's <cors> requires to
      // short-circuit). Empty body falls into the policy's invalid-emailHash
      // branch and returns 204 silently — no email is sent.
      id: "api",
      label: "Entrilia API",
      url: `${getAnonymousAuthBaseUrl()}/investor-portal/request-code`,
      targetHost: getUrlHostSafe(getAnonymousAuthBaseUrl()),
      init: {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      },
      passOn: "ok",
    },
  ];

  const { authEndpoint, userFlowSignIn } = getAuthSettings();
  const b2cDiscoveryUrl = buildB2CDiscoveryUrl(authEndpoint, userFlowSignIn);
  if (b2cDiscoveryUrl) {
    specs.push({
      id: "b2c",
      label: "Microsoft sign-in",
      url: b2cDiscoveryUrl,
      targetHost: getUrlHostSafe(b2cDiscoveryUrl),
      init: { mode: "cors", credentials: "omit", cache: "no-store" },
      passOn: "ok",
    });
  }

  return specs;
};

const offlineResult = (spec: ProbeSpec): ConnectionCheckResult => ({
  id: spec.id,
  label: spec.label,
  targetHost: spec.targetHost,
  ok: false,
  durationMs: 0,
  errorMessage: "Device reports offline (navigator.onLine === false)",
});

const probe = async (spec: ProbeSpec): Promise<ConnectionCheckResult> => {
  const startedAt = performance.now();
  try {
    const response = await fetch(spec.url, { ...spec.init, signal: AbortSignal.timeout(TIMEOUT_MS) });
    const durationMs = Math.round(performance.now() - startedAt);
    const ok = spec.passOn === "fetched" ? true : response.ok;
    return {
      id: spec.id,
      label: spec.label,
      targetHost: spec.targetHost,
      ok,
      durationMs,
      errorMessage: ok ? undefined : `HTTP ${response.status}`,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startedAt);
    const message = error instanceof Error ? `${error.name}: ${error.message}` : "Unknown error";
    return {
      id: spec.id,
      label: spec.label,
      targetHost: spec.targetHost,
      ok: false,
      durationMs,
      errorMessage: message,
    };
  }
};

export const runConnectionChecks = async (): Promise<ConnectionCheckResult[]> => {
  const specs = buildSpecs();
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return specs.map((spec) =>
      spec.id === "internet" ? offlineResult(spec) : { ...offlineResult(spec), errorMessage: "Skipped: device offline" }
    );
  }
  return Promise.all(specs.map(probe));
};

const BLOB_LABEL = "Document storage";
const BODY_PREVIEW_LIMIT = 2000;

const readResponseBody = async (response: Response): Promise<{ body: string; truncated: boolean }> => {
  try {
    const text = await response.text();
    if (text.length > BODY_PREVIEW_LIMIT) {
      return { body: text.slice(0, BODY_PREVIEW_LIMIT), truncated: true };
    }
    return { body: text, truncated: false };
  } catch {
    return { body: "", truncated: false };
  }
};

const collectHeaders = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  // Only CORS-safelisted headers (and anything in Access-Control-Expose-Headers) come
  // through. Anything else the browser silently strips before we get here.
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });
  return headers;
};

const probeUrlTarget = async (
  id: ConnectionCheckId,
  label: string,
  targetHost: string,
  probeUrl: string
): Promise<ConnectionCheckResult> => {
  const startedAt = performance.now();
  try {
    const response = await fetch(probeUrl, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    const durationMs = Math.round(performance.now() - startedAt);
    if (response.ok) {
      return {
        id,
        label,
        targetHost,
        ok: true,
        durationMs,
      };
    }
    const { body, truncated } = await readResponseBody(response);
    return {
      id,
      label,
      targetHost,
      ok: false,
      durationMs,
      errorMessage: `HTTP ${response.status}`,
      failureDetails: {
        url: probeUrl,
        statusCode: response.status,
        statusText: response.statusText,
        body,
        bodyTruncated: truncated,
        headers: collectHeaders(response),
      },
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startedAt);
    const errorName = error instanceof Error ? error.name : "Unknown";
    const errorDescription = error instanceof Error ? error.message : "Unknown error";
    return {
      id,
      label,
      targetHost,
      ok: false,
      durationMs,
      errorMessage: `${errorName}: ${errorDescription}`,
      failureDetails: {
        url: probeUrl,
        errorName,
        errorMessage: errorDescription,
      },
    };
  }
};

const probeBlobTarget = (target: BlobProbeTarget): Promise<ConnectionCheckResult> =>
  probeUrlTarget("blob", BLOB_LABEL, target.host, target.probeUrl);

// Probes the actual document URL the viewer failed on. A full (non-Range) GET on
// purpose: it makes an inspecting proxy fetch and scan the whole object, warming its
// verdict for the viewer's own GET. On success the body is never read.
export const probeDocumentUrl = (url: string): Promise<ConnectionCheckResult> =>
  probeUrlTarget("document", "This document", getUrlHostSafe(url), url);

export const runBlobProbes = async (targets: BlobProbeTarget[]): Promise<ConnectionCheckResult[]> => {
  if (targets.length === 0) {
    return [];
  }
  // Skip probes when offline. Synthetic ok:false results would misclassify a
  // transiently-offline user as Bucket 3 ("network blocks parts of the portal")
  // even though they're just disconnected — the cdn/api/b2c checks ran online
  // and passed, and the offline state is most often resolved before the next
  // sign-in attempt anyway.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return [];
  }
  return Promise.all(targets.map(probeBlobTarget));
};
